import {
  type Class,
  type Scalars,
  type Shindig,
} from '../__generated-gql-types__/globalTypes';
import { type CategoryTypeaheadCategoryFragment } from '../components/typeahead/__generated-gql-types__/CategoryTypeahead.generated';

import {
  __STAGING__,
  BASE_LELAND_GO_URL,
  COLLEGE_CATEGORY_URN,
  DEFERRED_MBA_SUBCATEGORY_SLUG,
  EVENTS_RELATIVE_URL,
  GRE_CATEGORY_URN,
  INVESTMENT_BANKING_CATEGORY_URN,
  MANAGEMENT_CONSULTING_CATEGORY_URN,
  MBA_CATEGORY_URN,
  PRIVATE_EQUIY_CATEGORY_URN,
  PRODUCT_MANAGEMENT_CATEGORY_URN,
  SWE_CATEGORY_URN,
} from './constants';
import { getCategorySRPUrl } from './hooks/useChangeCategory';
import { type SearchFiltersVariables } from './search';

export type OgDecoratedEvent<Event> = Event & {
  ogImg: Nullable<string>;
};

export enum EventTabs {
  UPCOMING = 'upcoming',
  PAST = 'past',
}

export const getCategoryForEvent = (
  itemCategorySlugs: string[],
  eventCategorySlugs: string[],
) => {
  const eventCategory = itemCategorySlugs.find((category) =>
    eventCategorySlugs.includes(category),
  );
  return eventCategory ?? eventCategorySlugs[0];
};

export type OgFetcher = (link: string) => Promise<Nullable<string>>;

export const getOgDecoratedEvents = async <Event extends { link: string }>(
  events: Event[],
  ogFetcher: OgFetcher,
): Promise<Array<OgDecoratedEvent<Event>>> =>
  Promise.all(
    events.map(async (event) => {
      let ogImg: Nullable<string> = null;
      try {
        ogImg = await ogFetcher(event.link);
      } catch (e) {
        console.warn(e);
      }
      return { ...event, ogImg };
    }),
  );

export const addUTMToEventLink = (link: string, utmMedium: string) => {
  try {
    const url = new URL(link);
    url.searchParams.set('utm_source', 'site');
    url.searchParams.set('utm_medium', utmMedium);
    return url.toString();
  } catch {
    return link;
  }
};

const STAGING_COHORT_COACH_SLUGS = ['john-koelliker', 'kyle-b', 'joe-tears'];

export const COHORT_BANNER_DATA_BY_CATEGORY_ID_OR_SUBCATEGORY_SLUG: Record<
  string,
  {
    date: Date;
    title: string;
    description: string;
    numSessions: number;
    url: string;
    coachSlugs: Possible<Array<Scalars['CoachUrn']['output']>>;
    preventShowing?: (searchVars: SearchFiltersVariables) => boolean;
  }
> = {
  [MBA_CATEGORY_URN]: {
    date: new Date('2024-10-21T12:00:00.000Z'),
    title: 'MBA Application Bootcamp',
    description:
      'Everything you need to gain acceptance to top MBA programs - taught by former admissions officers and experts from Harvard, Stanford, Wharton, and more. Seats are limited; secure yours today and use code THIRTY3 to get 33% off your bootcamp enrollment.',
    numSessions: 8,
    url: addUTMToEventLink(
      `${BASE_LELAND_GO_URL}/courses/mba-admissions-bootcamp`,
      'srp',
    ),
    coachSlugs: __STAGING__
      ? STAGING_COHORT_COACH_SLUGS
      : [
          'ben-lazaroff',
          'timothy-f',
          'john-koelliker',
          'allie-kronthal',
          'camden-robinson',
          'kerry-patriarca',
        ],
  },
  [DEFERRED_MBA_SUBCATEGORY_SLUG]: {
    date: new Date('2025-01-13T12:00:00.000Z'),
    title: 'Deferred MBA Application Bootcamp',
    description:
      'Everything you need to gain acceptance to top deferred MBA programs - taught by former admissions officers and experts from Harvard, Stanford, Wharton, and more. Seats are limited; secure yours today and use code THIRTY3 to get 33% off your bootcamp enrollment.',
    numSessions: 8,
    url: addUTMToEventLink(
      `${BASE_LELAND_GO_URL}/courses/deferred-mba-admissions-bootcamp`,
      'srp',
    ),
    coachSlugs: null,
  },
  [INVESTMENT_BANKING_CATEGORY_URN]: {
    date: new Date('2024-11-04T12:00:00.000Z'),
    title: 'Investment Banking Recruiting Bootcamp',
    description:
      'If you’re a passionate, driven person working to break into top investment banking roles, this bootcamp is the ultimate technical crash course you need to land an offer. Seats are limited; secure yours today and use code THIRTY3 to get 33% off your bootcamp enrollment.',
    numSessions: 6,
    url: addUTMToEventLink(
      `${BASE_LELAND_GO_URL}/courses/investment-banking-recruiting-bootcamp`,
      'srp',
    ),
    coachSlugs: null,
  },
  [MANAGEMENT_CONSULTING_CATEGORY_URN]: {
    date: new Date('2024-09-16T12:00:00.000Z'),
    title: 'Consulting Recruiting Bootcamp',
    description:
      'If you’re a passionate, driven person working to break into consulting, this bootcamp is the ultimate crash course you need to crush your interviews and land an offer. Seats are limited; secure yours today and use code THIRTY3 to get 33% off your bootcamp enrollment.',
    numSessions: 8,
    url: addUTMToEventLink(
      `${BASE_LELAND_GO_URL}/courses/management-consulting-interview-bootcamp`,
      'srp',
    ),
    coachSlugs: __STAGING__
      ? STAGING_COHORT_COACH_SLUGS
      : [
          'ben-lazaroff',
          'timothy-f',
          'cate-d',
          'alex-s-3',
          'nick-vanslooten',
          'wesley-bosco',
        ],
  },
  [COLLEGE_CATEGORY_URN]: {
    date: new Date('2024-08-29T12:00:00.000Z'),
    title: 'Essay Bootcamp for BYU Applicants (FREE)',
    description:
      'Craft a stellar BYU application with expert guidance from elite college admission consultants Admissions coaching worth $1200 given free to aspiring BYU students - that’s something to rise and shout about.',
    numSessions: 5,
    url: addUTMToEventLink(
      `${BASE_LELAND_GO_URL}/courses/essay-bootcamp-for-byu-applicants`,
      'srp',
    ),
    coachSlugs: __STAGING__
      ? STAGING_COHORT_COACH_SLUGS
      : ['christian-hansen', 'susie-boyce', 'john-koelliker', 'amanda-wirtz'],
    preventShowing: (searchVars) =>
      !searchVars.schoolsFilter?.includes(BYU_SCHOOL_URN),
  },
  [COLLEGE_CATEGORY_URN]: {
    date: new Date('2024-10-03T12:00:00.000Z'),
    title: 'College Application Bootcamp',
    description:
      'Craft a stellar application with expert guidance from elite college admission consultants; learn to discover and write your unique narrative in a compelling way. Admissions coaching worth $1200, free to aspiring college students.',
    numSessions: 4,
    url: addUTMToEventLink(
      `${BASE_LELAND_GO_URL}/courses/college-application-bootcamp`,
      'srp',
    ),
    coachSlugs: __STAGING__
      ? STAGING_COHORT_COACH_SLUGS
      : ['skyler-a-1', 'devery-d', 'charles-l', 'john-koelliker'],
  },
  [PRODUCT_MANAGEMENT_CATEGORY_URN]: {
    date: new Date('2025-01-20T12:00:00.000Z'),
    title: 'Product Management Recruiting Bootcamp',
    description:
      'If you’re a passionate, driven student working to break into top product roles, this bootcamp is the ultimate crash course you need to land an offer. Seats are limited; secure yours today and use code THIRTY3 to get 33% off your bootcamp enrollment.',
    numSessions: 8,
    url: addUTMToEventLink(
      `${BASE_LELAND_GO_URL}/courses/product-management-recruiting-bootcamp`,
      'srp',
    ),
    coachSlugs: __STAGING__
      ? STAGING_COHORT_COACH_SLUGS
      : ['amy-r', 'spencer-b-2', 'daniel-m-2', 'mike-m'],
  },
  [GRE_CATEGORY_URN]: {
    date: new Date('2025-01-14T12:00:00.000Z'),
    title: 'GRE Exam Prep Bootcamp',
    description:
      'If you’re a passionate, driven student working to land a top GRE score, this bootcamp is the ultimate crash course you need. Seats are limited; secure yours today. Use code THIRTY3 to get 33% off your bootcamp enrollment.',
    numSessions: 12,
    url: addUTMToEventLink(`${BASE_LELAND_GO_URL}/courses/gre-bootcamp`, 'srp'),
    coachSlugs: __STAGING__ ? STAGING_COHORT_COACH_SLUGS : ['bruce-h'],
  },
  [SWE_CATEGORY_URN]: {
    date: new Date('2025-02-13T12:00:00.000Z'),
    title: 'Software Engineering Recruiting Bootcamp',
    description:
      'If you’re a passionate, driven student working to break into top tech roles, this bootcamp is the ultimate crash course you need to land an software engineering offer. Seats are limited; secure yours today and use code THIRTY3 to get 33% off your bootcamp enrollment.',
    numSessions: 5,
    url: addUTMToEventLink(
      `${BASE_LELAND_GO_URL}/courses/software-engineering-recruiting-bootcamp`,
      'srp',
    ),
    coachSlugs: null,
  },
  [INVESTMENT_BANKING_CATEGORY_URN]: {
    date: new Date('2025-01-27T12:00:00.000Z'),
    title: 'Investment Banking Recruiting Bootcamp',
    description:
      'If you’re a passionate, driven person working to break into top investment banking roles, this bootcamp is the ultimate technical crash course you need to land an offer. Seats are limited; secure yours today and use code THIRTY3 to get 33% off your bootcamp enrollment.',
    numSessions: 5,
    url: addUTMToEventLink(
      `${BASE_LELAND_GO_URL}/courses/investment-banking-recruiting-bootcamp`,
      'srp',
    ),
    coachSlugs: __STAGING__
      ? STAGING_COHORT_COACH_SLUGS
      : ['naila-c', 'julia-mahterian', 'max-a', 'alejandro-a-1', 'max-m'],
  },
  [PRODUCT_MANAGEMENT_CATEGORY_URN]: {
    date: new Date('2025-01-22T12:00:00.000Z'),
    title: 'Product Management Recruiting Bootcamp',
    description:
      'If you’re a passionate, driven student working to break into top product roles, this bootcamp is the ultimate crash course you need to land an offer. Seats are limited; secure yours today and use code THIRTY3 to get 33% off your bootcamp enrollment.',
    numSessions: 7,
    url: addUTMToEventLink(
      `${BASE_LELAND_GO_URL}/courses/product-management-recruiting-bootcamp`,
      'srp',
    ),
    coachSlugs: __STAGING__
      ? STAGING_COHORT_COACH_SLUGS
      : ['amy-r', 'nina-prabhu', 'zane-homsi', 'daniel-m-2', 'mike-m'],
  },
  [MANAGEMENT_CONSULTING_CATEGORY_URN]: {
    date: new Date('2025-02-17T12:00:00.000Z'),
    title: 'Management Consulting Recruiting Bootcamp',
    description:
      'If you’re a passionate, driven student working to break into top management consulting firms, this bootcamp is the ultimate crash course you need to land an offer. Seats are limited; secure yours today and use code THIRTY3 to get 33% off your bootcamp enrollment.',
    numSessions: 7,
    url: addUTMToEventLink(
      `${BASE_LELAND_GO_URL}/courses/management-consulting-interview-bootcamp`,
      'srp',
    ),
    coachSlugs: __STAGING__
      ? STAGING_COHORT_COACH_SLUGS
      : [
          'nick-vanslooten',
          'caitlin-b',
          'jeremy-scharf',
          'ian-g',
          'emily-h-1',
          'cate-d',
          'ben-lazaroff',
        ],
  },
  [PRIVATE_EQUIY_CATEGORY_URN]: {
    date: new Date('2025-02-11T12:00:00.000Z'),
    title: 'Private Equity Recruiting Bootcamp',
    description:
      'If you’re a passionate, driven student working to break into private equity roles, this bootcamp is the ultimate crash course you need to land an offer. Seats are limited; secure yours today and use code THIRTY3 to get 33% off your bootcamp enrollment.',
    numSessions: 6,
    url: addUTMToEventLink(
      `${BASE_LELAND_GO_URL}/courses/private-equity-recruiting-bootcamp`,
      'srp',
    ),
    coachSlugs: __STAGING__
      ? STAGING_COHORT_COACH_SLUGS
      : ['arielle-f', 'max-a', 'nick-l', 'max-m'],
  },
};

export const BYU_SCHOOL_URN = 'urn:school:60a4a82235b4a535fc5f6bb8';

export const isEventShindig = <
  S extends Pick<Shindig, '__typename'>,
  C extends Pick<Class, '__typename'>,
>(
  event: S | C | undefined,
): event is S => {
  return event?.__typename === 'Shindig';
};

export const getEventUrl = (
  category: Possible<CategoryTypeaheadCategoryFragment>,
) => {
  return getCategorySRPUrl(category, EVENTS_RELATIVE_URL);
};
